import { MinusOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { Avatar, Button, Tooltip, Typography } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useHistory } from 'react-router-dom'

import { UserHoverCard } from '@/components'
import { GetWebsitesDocument, WebsiteInfoFragment, useWebsiteCommonToggleMutation } from '@/generated/graphql'
import { useIsSystemAdmin } from '@/modules/admin/admin.hooks'
import { WebsiteSetCommonContext } from '@/modules/app'
import { routes } from '@/routes'

import { WebsiteCreateEditModal } from '../website-modal/website-create-edit-modal.component'
import s from './website-card.module.less'

export const WebsiteCard: React.FC<{ website: WebsiteInfoFragment }> = ({ website }) => {
  const history = useHistory()

  const isSystemAdmin = useIsSystemAdmin()

  const { visible: setCommonVisible } = React.useContext(WebsiteSetCommonContext)

  const [websiteCommonToggle] = useWebsiteCommonToggleMutation({
    refetchQueries: [getOperationName(GetWebsitesDocument)!],
  })

  const title = <span className={cls(s.websiteTitle, { [s.edit]: isSystemAdmin })}>{website.title}</span>

  const header = (
    <div className={s.header}>
      <div className={s.title}>
        {isSystemAdmin ? (
          <WebsiteCreateEditModal websiteCategoryId={website.websiteCategoryId} website={website}>
            {title}
          </WebsiteCreateEditModal>
        ) : (
          title
        )}
        {website.description && (
          <Tooltip placement='top' title={website.description}>
            <QuestionCircleOutlined className={s.description} />
          </Tooltip>
        )}
      </div>
      <div className={s.maintainers}>
        {website.maintainers.map((maintainer) => (
          <UserHoverCard key={maintainer.userId} email={maintainer.email}>
            <Avatar
              onClick={() => history.push(routes.users.page(maintainer.email))}
              size={20}
              src={maintainer.avatarUrl}
            />
          </UserHoverCard>
        ))}
      </div>
    </div>
  )

  const content = (
    <div className={s.content}>
      <div className={s.url}>
        <div className={s.link}>
          <a target='_blank' href={website.url} rel='noopener noreferrer'>
            {website.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </a>
        </div>
        <Typography.Text className={s.copy} copyable={{ text: website.url, tooltips: false }} />
      </div>
    </div>
  )

  const actions = (
    <Button
      size='small'
      onClick={() =>
        websiteCommonToggle({
          variables: {
            websiteId: website.websiteId,
          },
        })
      }
      style={{ display: setCommonVisible ? undefined : 'none' }}
      shape='circle'
      icon={website.isCommon ? <MinusOutlined /> : <PlusOutlined />}
    />
  )

  return (
    <div
      className={s.card}
      style={{ display: setCommonVisible ? 'flex' : undefined, alignItems: setCommonVisible ? 'center' : undefined }}
    >
      <div className={s.setCommonAction}>{actions}</div>
      <div className={s.cardInner}>
        <div className={s.cardContent}>
          {header}
          {content}
        </div>
      </div>
    </div>
  )
}
